import { useCallback, useEffect, useState } from 'react'

interface UseAuthOptions {
  getUserInfoFn?: (accessToken: string) => Promise<any>
  onAuthError?: (error: unknown) => void
}

export const useAuth = (authManager: any, options: UseAuthOptions = {}) => {
  const { getUserInfoFn, onAuthError } = options

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [userInfo, setUserInfo] = useState<any>(null)

  /** Check login status on mount */
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const loggedIn = await authManager.isLoggedIn()
        setIsLoggedIn(loggedIn)

        if (loggedIn) {
          let info: any = null
          if (getUserInfoFn) {
            const accessToken = await authManager.getValidAccessToken()
            if (accessToken) {
              info = await getUserInfoFn(accessToken)
            }
          }
          if (!info) {
            info = await authManager.getUserInfo()
          }
          setUserInfo(info)
        }
      } catch (error) {
        console.error('[useAuth] Failed to check auth status:', error)
        setIsLoggedIn(false)
        setUserInfo(null)
        if (onAuthError) {
          onAuthError(error)
        }
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [authManager, getUserInfoFn, onAuthError])

  /** Login with tokens */
  const login = useCallback(
    async (accessToken: string, refreshToken: string | null = null) => {
      try {
        await authManager.setTokens(accessToken, refreshToken)
        await authManager.setLoggedInFlag(true)

        let info: any = null
        if (getUserInfoFn) {
          info = await getUserInfoFn(accessToken)
        }
        if (!info) {
          info = await authManager.getUserInfo()
        }
        setUserInfo(info)
        setIsLoggedIn(true)

        return true
      } catch (error) {
        console.error('[useAuth] Login failed:', error)
        if (onAuthError) {
          onAuthError(error)
        }
        return false
      }
    },
    [authManager, getUserInfoFn, onAuthError],
  )

  /** Logout */
  const logout = useCallback(async () => {
    try {
      await authManager.logout()
      setIsLoggedIn(false)
      setUserInfo(null)
    } catch (error) {
      console.error('[useAuth] Logout failed:', error)
    }
  }, [authManager])

  /** Get valid access token */
  const getAccessToken = useCallback(async () => {
    try {
      return await authManager.getValidAccessToken()
    } catch (error) {
      console.error('[useAuth] Failed to get access token:', error)
      if (onAuthError) {
        onAuthError(error)
      }
      return null
    }
  }, [authManager, onAuthError])

  /** Refresh auth state (call after manual token updates) */
  const refresh = useCallback(async () => {
    try {
      const loggedIn = await authManager.isLoggedIn()
      setIsLoggedIn(loggedIn)

      if (loggedIn) {
        let info: any = null
        if (getUserInfoFn) {
          const accessToken = await authManager.getValidAccessToken()
          if (accessToken) {
            info = await getUserInfoFn(accessToken)
          }
        }
        if (!info) {
          info = await authManager.getUserInfo()
        }
        setUserInfo(info)
      } else {
        setUserInfo(null)
      }
    } catch (error) {
      console.error('[useAuth] Failed to refresh auth state:', error)
      if (onAuthError) {
        onAuthError(error)
      }
    }
  }, [authManager, getUserInfoFn, onAuthError])

  return {
    isLoggedIn,
    isLoading,
    userInfo,
    login,
    logout,
    getAccessToken,
    refresh,
  }
}
